import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cronApi } from "./axios";
import { 
    DataI,
    ServiceResponseI,
    PropsCronI,
    CronI,
    ObjPostI
} from '../../interfaces';
import {
    ValidateCurrentToken,
    LogoutSystem, 
} from '../../helpers';
import { AxiosError } from 'axios';


const getCrons = async ({
    pagination, 
    sorting, 
    globalFilter,
}:PropsCronI):Promise<ServiceResponseI<DataI<CronI>>> => {

    try{ 
        
        const validateToken = ValidateCurrentToken();

        if(!validateToken) throw new Error('TOKEN INVALIDO');

        const params = new URLSearchParams();

        params.append('page', (pagination!.pageIndex + 1).toString());
        params.append('limit', pagination!.pageSize.toString());
        params.append('search', globalFilter ?? '');
        params.append('sorting', JSON.stringify(sorting ?? []));

        //headers: { 'Authorization': validateToken
        const { data } = await cronApi.get<ServiceResponseI<DataI<CronI>>>('',{ params, headers: { 'Authorization': validateToken}});
        return data;
    
    }catch(err){
        const obj = (err as AxiosError).response;

        if(obj?.status === 401) {
            
            LogoutSystem();
        }

        throw new Error((err as Error).message)
    }
}

export const getAllCrons = async ():Promise<ServiceResponseI<CronI[]>> =>{
    try{ 
        const validateToken = ValidateCurrentToken();
        if(!validateToken) throw new Error('TOKEN INVALIDO');


        const { data } = await cronApi.get<ServiceResponseI<CronI[]>>(`/all-no-pagination`,{ headers: { 'Authorization': validateToken}});
        
        return data;

    }catch(err){
        const obj = (err as AxiosError).response;
        if(obj?.status === 401) {
            
            LogoutSystem();
        }
        throw new Error((err as Error).message)
    }
}

// Función para obtener un cron por ID (actualizada para usar con useQuery)
const getCronById = async (id: number): Promise<ServiceResponseI<CronI>> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const { data } = await cronApi.get<ServiceResponseI<CronI>>(`/${id}`, { headers: { 'Authorization': validateToken } });
        return data;
    } catch (err) {
        const obj = (err as AxiosError).response;
        if (obj?.status === 401) {
            LogoutSystem();
        }
        throw new Error((obj?.data as Error).message)
    }
}

export const runCronById = async (id: number): Promise<ServiceResponseI<CronI>> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const { data } = await cronApi.get<ServiceResponseI<CronI>>(`/run/${id}`, { headers: { 'Authorization': validateToken } });
        return data;
    } catch (err) {
        const obj = (err as AxiosError).response;
        if (obj?.status === 401) {
            LogoutSystem();
        }
        throw new Error((obj?.data as Error).message)
    }
}

// Función para crear un nuevo cron (para usar con useMutation)
const createCron = async<T>(formData: T): Promise<ServiceResponseI<ObjPostI>> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const { data } = await cronApi.post<ServiceResponseI<ObjPostI>>("", formData, { headers: { 'Authorization': validateToken } });
        return data;
    } catch (err) {
        const obj = (err as AxiosError).response;
        if (obj?.status === 401) {
            LogoutSystem();
        }
        throw new Error((obj?.data as Error).message)
    }
}

// Función para actualizar un cron (para usar con useMutation)
const updateCron = async({ id, updateForms }: { id: number, updateForms: any }): Promise<ServiceResponseI<string>> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const { data } = await cronApi.put<ServiceResponseI<string>>(`/${id}`, updateForms, { headers: { 'Authorization': validateToken } });
        return data;
    } catch (err) {
        const obj = (err as AxiosError).response;
        if (obj?.status === 401) {
            LogoutSystem();
        }
        throw new Error((obj?.data as Error).message)
    }
}

// Función para eliminar un cron (para usar con useMutation)
const deleteCron = async (id: number): Promise<ServiceResponseI<string>> => {
    try {
        const validateToken = ValidateCurrentToken();
        if (!validateToken) throw new Error('TOKEN INVALIDO');
        const { data } = await cronApi.delete<ServiceResponseI<string>>(`/${id}`, { headers: { 'Authorization': validateToken } });
        return data;
    } catch (err) {
        const obj = (err as AxiosError).response;
        if (obj?.status === 401) {
            LogoutSystem();
        }
        throw new Error((obj?.data as Error).message)
    }
}

// Hook para obtener la lista de crons
export const useCrons = ({
    sorting,
    pagination,
    globalFilter,
}:PropsCronI)=>{
    
    const cronsQuery = useQuery(
        ["crons", { pagination, sorting, globalFilter }],
        ()=>getCrons({ pagination, sorting, globalFilter }),
        {
            refetchOnWindowFocus:false,
            keepPreviousData:true
        }
    );

    return {
        cronsQuery,
    };
}

// Hook para obtener un cron por ID
export const useCronById = (id: number) => {
    return useQuery(
        ["cron", id],
        () => getCronById(id),
        {
            enabled: !!id, // Solo se ejecuta si hay un ID
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 5, // 5 minutos
        }
    );
}

// Hook para crear un cron (useMutation)
export const useCreateCron = () => {
    const queryClient = useQueryClient();
    
    return useMutation(
        (formData: any) => createCron(formData),
        {
            onSuccess: () => {
                // Invalidar la caché de crons para refrescar la lista
                queryClient.invalidateQueries(["crons"]);
            },
        }
    );
}

// Hook para actualizar un cron (useMutation)
export const useUpdateCron = () => {
    const queryClient = useQueryClient();
    
    return useMutation(
        (params: { id: number, updateForms: any }) => updateCron(params),
        {
            onSuccess: (_, variables) => {
                // Invalidar las consultas específicas
                queryClient.invalidateQueries(["crons"]);
                queryClient.invalidateQueries(["cron", variables.id]);
            },
        }
    );
}

// Hook para eliminar un cron (useMutation)
export const useDeleteCron = () => {
    const queryClient = useQueryClient();
    
    return useMutation(
        (id: number) => deleteCron(id),
        {
            onSuccess: () => {
                // Invalidar la caché de crons para refrescar la lista
                queryClient.invalidateQueries(["crons"]);
            },
        }
    );
}